import {
  FilterBody,
  FilterQuery,
  Pagination,
  SortOption,
  YesObject,
  usePagination
} from '@yes.technology/react-toolkit'
import { useCallback, useState } from 'react'

import { merge } from 'lodash'
import api from 'utils/api'
import { FetchYesObjectsResponse } from 'utils/apiTypes'
import { resolveQuery } from 'modules/filteraction/FilteractionResolver'
import { useQuery } from '@tanstack/react-query'
import fetchFilteraction from 'modules/filteraction/fetchFilteraction'

type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>

type SerializibleKey = string | { [x: string]: string }

type UseFetchYesObjectsArgs = {
  initialPagination?: Partial<Pagination>
  initialSortOptions?: SortOption[]
  filterBody?: Optional<
    FilterBody,
    'pagination' | 'orderby' | 'skip_metadata' | 'project_fields'
  >
  showLoading?: boolean
  filteractionUuid?: string
  enabled?: boolean
  interpolationObject?: Record<string, unknown>
  shouldMergeFilters?: boolean
  additionalQueryKeyParams?: SerializibleKey[]
  refetchInterval?: number
}

export const INITIAL_PAGINATION = { limit: 10, offset: 0 }

export const INITIAL_SORT_OPTIONS: SortOption[] = [
  {
    date_creation_row: 'desc'
  }
]

const emptyArr: YesObject[] = []

export default function useFetchYesObjects<T extends YesObject>({
  initialPagination = INITIAL_PAGINATION,
  initialSortOptions = INITIAL_SORT_OPTIONS,
  filterBody,
  filteractionUuid,
  showLoading = false,
  enabled = true,
  shouldMergeFilters = false,
  additionalQueryKeyParams = [],
  refetchInterval
}: UseFetchYesObjectsArgs) {
  const { pagination, setPage } = usePagination({
    initialPagination
  })

  const [sortOptions, setSortOptions] =
    useState<SortOption[]>(initialSortOptions)

  const { data, error, isLoading, isFetching, refetch, isFetched } = useQuery<
    FetchYesObjectsResponse,
    Error
  >({
    queryKey: [
      'YesObjects',
      pagination.limit,
      pagination.offset,
      { filterBody, showLoading, sortOptions },
      filteractionUuid,
      ...additionalQueryKeyParams
    ],
    queryFn: async () => {
      if (!filterBody && !filteractionUuid)
        return Promise.reject(
          new Error('Either filterBody or filteractionUuid must be provided.')
        )

      let callingFilterBody = {
        ...filterBody,
        pagination,
        orderby: sortOptions
      } as FilterBody

      if (filteractionUuid) {
        const filteraction = await fetchFilteraction(
          filteractionUuid,
          showLoading
        )

        if (!filteraction) {
          return Promise.reject(new Error('Filteraction not found.'))
        }

        const filteractionQuery = await resolveQuery(
          JSON.parse(filteraction?.query) as FilterQuery
        )

        const mergedFilterQuery = {
          $and: [
            {
              ...merge(filteractionQuery ?? {}, filterBody?.query ?? {})
            }
          ]
        }

        const finalQuery = shouldMergeFilters
          ? mergedFilterQuery
          : filteractionQuery

        const objectclassUuid = (
          filteraction.filter_action_objectclass as YesObject[]
        )[0]?.uuid

        callingFilterBody = {
          query: finalQuery,
          objectclass: objectclassUuid,
          skip_metadata: filterBody?.skip_metadata,
          skip_relations: filterBody?.skip_relations,
          project_fields: filterBody?.project_fields,
          orderby: sortOptions,
          pagination
        }
      }

      return api.postObjectsFilter(callingFilterBody, showLoading, false)
    },
    enabled,
    refetchInterval
  })

  const onSetSortOptions = useCallback(
    (sortOptions: SortOption[]) => {
      if (sortOptions?.length > 0) {
        const [sortOptionKey] = sortOptions
        const [sortOptionValue] = Object.values(sortOptionKey)

        setSortOptions(sortOptionValue ? sortOptions : initialSortOptions)
      }
    },
    [initialSortOptions]
  )

  return {
    yesObjects: (data?.objects || emptyArr) as T[],
    metadata: data?.metadata,
    isLoading,
    isFetching,
    isFetched,
    fetchYesObjects: refetch,
    pagination: data?.pagination || pagination,
    error,
    totalObjects: data?.pagination?.total || 0,
    setPage,
    sortOptions,
    setSortOptions: onSetSortOptions
  }
}
